import React from "react";
import { AlertTriangle, RotateCcw, X } from "lucide-react";

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogClose,
} from "@/components/ui/Modal";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import { Callout } from "@/components/ui/Callout";
import { plural } from "@/utils/formatText";

import { SOLID_DANGER } from "../constants";
import RevisionSqlBlock from "./RevisionSqlBlock";

/**
 * Confirm a one-revision schema downgrade.
 *
 * This replaced a plain confirm dialog whose whole description was "whatever
 * that revision added — a column, a table, the data in it — goes with it, and
 * may not be recoverable". True, and not enough to decide on: the statements the
 * downgrade will run are knowable, so they are shown.
 *
 * Two facts the old dialog could not carry:
 *
 * - **Six migrations here have a downgrade that does nothing.** They did their
 *   work through raw SQL and the inverse was never written, so a downgrade
 *   reports success and reverts nothing. That gets said outright.
 * - **What the downgrade actually does to data.** For the revision that split
 *   `jobs.experience` into a range, the SQL says
 *   `UPDATE jobs SET experience = experience_min` and then drops
 *   `experience_max` — so every ceiling is discarded. Visible, rather than
 *   requiring somebody to have written it down.
 *
 * The interaction is unchanged from the dialog it replaces: cancel, or confirm.
 * No new gate — this is the same decision, better informed.
 */
const AlembicDowngradeModal = ({
    target,
    catalog,
    dedicatedCount,
    downgrading,
    onCancel,
    onConfirm,
}) => {
    const isAll = target?.key === "all";
    const where = isAll
        ? plural(
              dedicatedCount,
              "dedicated tenant database",
              "dedicated tenant databases"
          )
        : target?.tenantSlug || target?.label || "this database";

    // Fleet-wide, each database sits on its own revision, so there is no single
    // one to describe — the same reason every other preview on this screen is
    // skipped for 'all'.
    const revision = isAll ? undefined : catalog?.revision(target?.alembicCurrent);
    const irreversible = revision?.isReversible === false;

    return (
        <Dialog
            open={!!target}
            onOpenChange={(o) => !o && !downgrading && onCancel?.()}
        >
            <DialogContent className="w-[340px] md:w-[720px] max-h-[85vh] flex flex-col">
                <DialogHeader className="relative px-5 py-3 border-b border-border-primary shrink-0">
                    <DialogTitle className="text-[15px] font-semibold text-text-primary pr-8">
                        Downgrade the schema by one revision?
                    </DialogTitle>
                    <DialogDescription className="text-[12px] text-text-secondary mt-0.5 pr-8">
                        {where}
                        {revision?.title ? ` · undoing "${revision.title}"` : ""}
                    </DialogDescription>
                    <DialogClose
                        disabled={downgrading}
                        aria-label="Close"
                        className="absolute right-5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <X size={16} strokeWidth={3} aria-hidden="true" />
                    </DialogClose>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto custom-scrollbar px-5 py-4 space-y-4">
                    <Callout tone="warning" icon={AlertTriangle} size="xs">
                        <span className="font-medium block mb-0.5">
                            This can lose data
                        </span>
                        Whatever the revision added — a column, a table, the rows in
                        it — goes with it, and may not be recoverable. A database that
                        fails does not stop the rest.
                    </Callout>

                    {irreversible && (
                        <Callout tone="warning" icon={AlertTriangle} size="xs">
                            <span className="font-medium block mb-0.5 text-tone-rose">
                                This revision cannot be undone
                            </span>
                            Its downgrade contains no operations at all, so this will
                            report success and change nothing. The work it did —{" "}
                            {plural(
                                revision.upgradeRawSql ?? 0,
                                "raw SQL step",
                                "raw SQL steps"
                            )}{" "}
                            — has to be reversed by hand.
                        </Callout>
                    )}

                    {isAll ? (
                        <Callout tone="warning" icon={AlertTriangle} size="xs">
                            <span className="font-medium block mb-0.5">
                                No preview for a fleet-wide downgrade
                            </span>
                            Each database is on its own revision, so what gets undone
                            differs per database. Open one row's Schema column to read
                            its downgrade before running this.
                        </Callout>
                    ) : (
                        revision && (
                            <div>
                                <div className="text-xs font-medium text-text-secondary mb-2">
                                    What the downgrade will run
                                </div>
                                <RevisionSqlBlock
                                    revision={revision.revision}
                                    direction="downgrade"
                                    fallbackOperations={revision.downgradeOperations}
                                />
                            </div>
                        )
                    )}
                </div>

                <div className="border-t border-border-primary px-5 py-3 flex justify-end gap-2">
                    <DialogClose
                        disabled={downgrading}
                        render={
                            <Button
                                variant="outline"
                                type="button"
                                disabled={downgrading}
                            >
                                Cancel
                            </Button>
                        }
                    />
                    <Button
                        onClick={onConfirm}
                        disabled={downgrading}
                        className={SOLID_DANGER}
                    >
                        <RotateCcw size={14} className="mr-1" aria-hidden="true" />
                        {downgrading ? "Downgrading…" : "Downgrade"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AlembicDowngradeModal;
